/**
 * Helper para manejo centralizado de autenticación
 * Utilidades para detectar y manejar errores de sesión expirada
 */

/**
 * Maneja errores de API y redirige al login si es necesario
 * @param {Error} error - Error capturado
 * @param {Object} msalInstance - Instancia de MSAL para hacer logout
 * @returns {boolean} - True si se manejó el error de autenticación
 */
export const handleAuthError = (error, msalInstance) => {
    if (error?.needsLogin) {
        console.warn('🔒 Sesión expirada. Iniciando proceso de logout...');
        
        // Intentar logout con redirect
        msalInstance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin
        }).catch(logoutError => {
            console.error('Error en logout redirect:', logoutError);
            // Fallback: limpiar cache y recargar
            msalInstance.clearCache();
            window.location.href = '/';
        });
        
        return true; // Error de auth manejado
    }
    
    return false; // No es error de auth
};

/**
 * Wrapper para llamadas API con manejo automático de expiración
 * @param {Function} apiCall - Función que hace la llamada al API
 * @param {Object} msalInstance - Instancia de MSAL
 * @returns {Promise} - Resultado de la llamada o maneja el error
 */
export const withAuthHandler = async (apiCall, msalInstance) => {
    try {
        return await apiCall();
    } catch (error) {
        const handled = handleAuthError(error, msalInstance);
        if (!handled) {
            throw error; // Re-lanzar si no es error de auth
        }
    }
};

/**
 * Verifica si un token está próximo a expirar
 * @param {Object} account - Cuenta activa de MSAL
 * @param {number} minutesThreshold - Minutos antes de expiración para considerar como "próximo"
 * @returns {boolean}
 */
export const isTokenNearExpiration = (account, minutesThreshold = 5) => {
    if (!account?.idTokenClaims?.exp) {
        return false;
    }
    
    const expirationTime = account.idTokenClaims.exp * 1000; // Convert to ms
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;
    const minutesUntilExpiration = timeUntilExpiration / (1000 * 60);
    
    return minutesUntilExpiration <= minutesThreshold;
};

/**
 * Muestra notificación visual de sesión próxima a expirar
 * @param {number} minutesRemaining - Minutos restantes
 */
export const notifySessionExpiring = (minutesRemaining) => {
    console.warn(`⏰ Su sesión expirará en ${minutesRemaining} minutos`);
    // Aquí podrías integrar con un sistema de notificaciones toast
};
